import { CheckedChangeEvent, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { modDisabled } from '@sunwebgroup/ui-components/uds'
import {
  Checkbox as CheckboxComponent,
  type CheckboxState,
  focusWithinOutline,
  Icon,
  IconGlyph,
  RadioButton,
  resetFocusOutline,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import styled from 'styled-components'

interface ListFilterItemCommonProps {
  label: string
  glyph?: IconGlyph
  counter?: number
  disabled?: boolean
}

interface ListFilterItemCheckboxProps extends ListFilterItemCommonProps {
  checked: boolean
  onChange: (checked: CheckedChangeEvent) => void
  value: string
}

interface ListFilterItemRadioProps extends ListFilterItemCommonProps {
  value: string
}

const Wrapper = styled.label<{ $disabled?: boolean }>`
  display: flex;
  padding: ${space('primitives_space_12')};
  width: 100%;
  gap: ${space('primitives_space_8')};
  border-radius: ${tokPx('primitives_corner_radius_m')};
  border: ${tokPx('semantics_input_fields_border_width')} solid ${tok('semantics_selectors_border_color')};
  ${focusWithinOutline()}
  cursor: pointer;
  margin-bottom: ${space('primitives_space_8')};

  ${CheckboxComponent}, ${RadioButton} {
    ${resetFocusOutline()}
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${tok('semantics_selectors_hover_border_color')};
  }


  ${({ $disabled }) => $disabled && modDisabled($disabled)}
`

const Label = styled(Typography)`
  display: flex;
  align-self: stretch;
  flex: 1;
`

function ListFilterItemContent({ label, glyph, counter }: ListFilterItemCommonProps) {
  return (
    <>
      {glyph && <Icon glyph={glyph} size={20} />}
      <Label variant="body-m-regular-tight">
        {label}
      </Label>
      {counter !== undefined && (
        <Typography variant="body-m-regular-tight" $color="semantics_content_color">
          {counter}
        </Typography>
      )}
    </>
  )
}

function Checkbox({ label, glyph, counter, checked, onChange, value, disabled, ...rest }: ListFilterItemCheckboxProps) {
  const handleOnchange = (state: CheckboxState) => {
    onChange?.({
      checked: state,
      name: label,
      value,
    })
  }

  return (
    <Wrapper $disabled={disabled} {...rest}>
      <CheckboxComponent checked={checked} onCheckedChange={handleOnchange} />
      <ListFilterItemContent label={label} glyph={glyph} counter={counter} />
    </Wrapper>
  )
}

function Radio({ label, glyph, counter, value, disabled, ...rest }: ListFilterItemRadioProps) {
  return (
    <Wrapper $disabled={disabled} {...rest}>
      <RadioButton value={value} />
      <ListFilterItemContent label={label} glyph={glyph} counter={counter} />
    </Wrapper>
  )
}

export default { Checkbox, Radio }
